import { useIntersectionObserver } from '@ui/hooks/useIntersectionObserver';
import { Text } from '@ui/lib/park/ui/text';
import {
  HistoryStatus,
  TradeHistory,
} from '@ui/services/backend/hooks/useTradeHistory/type';
import { useTranslation } from 'next-i18next';
import { ReactNode, useDeferredValue, useMemo, useState } from 'react';
import { Box, Grid, HStack, HstackProps, Stack } from 'styled-system/jsx';

import { HistoryCard } from './HistoryCard';
import {
  HistoryCardContextProvider,
  HistoryHoverProvider,
} from './historyCardContext';
import { ViewExecutedOnly } from './ViewExecutedOnly';

const filterIsExecuted = (item: TradeHistory) => {
  return item.status === HistoryStatus.EXECUTED;
};
export const HistoryList = ({
  history,
  emptyView,
  infiniteLoader,
  showExecutedOnlyToggle = false,
}: {
  history: TradeHistory[];
  emptyView: ReactNode;
  infiniteLoader: ReactNode;
  showExecutedOnlyToggle?: boolean;
}) => {
  const [isExecutedOnly, setIsExecutedOnly] = useState(false);

  const isExecutedOnlyDeferred = useDeferredValue(isExecutedOnly);
  const filteredHistory = useMemo(() => {
    const filters: ((item: TradeHistory) => boolean)[] = [];
    if (isExecutedOnlyDeferred) {
      filters.push(filterIsExecuted);
    }

    return filters.reduce((acc, filter) => acc.filter(filter), history);
  }, [history, isExecutedOnlyDeferred]);

  return (
    <HistoryHoverProvider>
      <Stack gap="0">
        {showExecutedOnlyToggle && (
          <ViewExecutedOnly
            css={{
              justifyContent: 'end',
              height: '0',
              // float into the 40px V2 tab bar, vertically centered
              transform: 'translateY(-20px) translateX(-10px)',
            }}
            display={{ base: 'none', desktop: 'flex' }}
            checked={isExecutedOnly}
            onCheckedChange={(v) => setIsExecutedOnly(v.checked)}
          />
        )}
        <Grid
          gridTemplateColumns="minmax(60px, 6fr) minmax(100px, 10fr) minmax(110px, 10fr) minmax(140px, 11fr) minmax(80px, 9fr) minmax(150px, 14fr) minmax(120px, 11fr)"
          columnGap={0}
          rowGap={0}
          px={4}
          display={{ base: 'none', tablet: 'none', desktop: 'grid' }}
          h="32px"
          alignItems="center"
          borderBottom="1px solid"
          bg="v2.bg.app"
          borderBottomColor="v2.border.hairline"
          position="sticky"
          top={0}
          zIndex={10}
        >
          <Stack gap={1}>
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Time
            </Text>
          </Stack>
          <Stack gap={1}>
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Pair & Side
            </Text>
          </Stack>
          <Stack gap={1} alignItems="flex-start">
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Size
            </Text>
          </Stack>
          <Stack gap={1} alignItems="flex-start">
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Collateral
            </Text>
          </Stack>
          <Stack gap={1} alignItems="flex-start">
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Action
            </Text>
          </Stack>
          <Stack gap={1} alignItems="flex-start">
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Detail
            </Text>
          </Stack>
          <Stack gap={1} alignItems="flex-start">
            <Text textStyle="v2.tableHeader" color="v2.text.subtle">
              Realized PnL
            </Text>
          </Stack>
        </Grid>

        <Stack
          gap="0"
          px={{ base: '10px', desktop: 0 }}
          pt={{ base: 2, desktop: 0 }}
        >
          {filteredHistory.map((item, index) => (
            <RenderHistoryCard key={item.id} history={item} />
          ))}
        </Stack>
        {filteredHistory.length === 0 && emptyView}
        {filteredHistory.length > 0 && infiniteLoader}
      </Stack>
    </HistoryHoverProvider>
  );
};
const RenderHistoryCard = ({ history }: { history: TradeHistory }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const { intersectionRef } = useIntersectionObserver<HTMLDivElement>({
    onIntersect: () => {
      setShouldFetch(true);
    },
    rootMargin: '0px 0px 100px',
  });

  return (
    <HistoryCardContextProvider isQueryEnabled={shouldFetch}>
      <Box ref={intersectionRef}>
        <HistoryCard history={history} />
      </Box>
    </HistoryCardContextProvider>
  );
};
